"use client";

import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

// The sign-in modal's step change. When `stepKey` changes (email to code,
// email to the other sign-in choices, and back), the frame animates its
// height from the previous step's height to the new one and replays a short
// fade-slide on the content, so the modal never jumps between steps. The
// children stay mounted: the frame animates around them instead of keying
// them, so a form's own state (the pending code, the flow token) survives.
//
// Timing lives in SignInModalStyles.tsx (.twt-step-frame, .twt-step-content):
// 150ms, ease-out. prefers-reduced-motion turns both off there and the
// height is applied at once here.
type Props = {
  stepKey: string;
  /** Which way the step moves; the entering content slides from that side. */
  direction?: "forward" | "back";
  /** Goes on the content box, the element that lays the steps out. */
  className?: string;
  children: ReactNode;
};

export const STEP_TRANSITION_MS = 150;

// The entering fade-slide is a transform on the content box, and a transform
// makes that box the containing block of every absolutely positioned element
// inside it. The back arrow at the modal's top left is positioned against the
// modal, so inside the content it would sit against the content for the
// 150ms of the animation and jump to the corner when it ends. The frame keeps
// a place for it outside the content box, and a step wraps its arrow in
// SignInStepBackControl, which renders it there, so the arrow never moves.
const BackControlPlaceContext = createContext<HTMLElement | null>(null);

/**
 * A step's back arrow. Inside a frame it renders in the frame's place outside
 * the animated content (a portal, once the frame has mounted); anywhere else,
 * and on the server, it renders where it is written.
 */
export function SignInStepBackControl({ children }: { children: ReactElement }) {
  const place = useContext(BackControlPlaceContext);
  return place ? createPortal(children, place) : children;
}

function reducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export function SignInStepFrame({
  stepKey,
  direction = "forward",
  className = "",
  children,
}: Props) {
  const frameRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const previousKey = useRef(stepKey);
  const settledHeight = useRef<number | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const [backControlPlace, setBackControlPlace] = useState<HTMLElement | null>(
    null
  );

  // Remember the height of the step that is showing, so a change can start
  // from it. Heights seen mid-animation are skipped.
  useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame || typeof ResizeObserver === "undefined") return;
    settledHeight.current = frame.getBoundingClientRect().height;
    const observer = new ResizeObserver(() => {
      if (frame.classList.contains("is-animating")) return;
      settledHeight.current = frame.getBoundingClientRect().height;
    });
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (previousKey.current === stepKey) return;
    previousKey.current = stepKey;
    const frame = frameRef.current;
    const content = contentRef.current;
    if (!frame || !content) return;
    if (reducedMotion()) {
      settledHeight.current = frame.getBoundingClientRect().height;
      return;
    }

    // A change that lands mid-animation continues from where the frame is.
    const animating = frame.classList.contains("is-animating");
    const from = animating
      ? parseFloat(getComputedStyle(frame).height)
      : settledHeight.current;
    cleanupRef.current?.();

    frame.style.height = "auto";
    const to = frame.getBoundingClientRect().height;

    // Replay the entering fade-slide on the new content (the class carries
    // the animation, so the first paint of the modal does not run it).
    content.classList.remove("is-entering");
    void content.offsetWidth;
    content.classList.add("is-entering");

    if (from == null || Math.abs(to - from) < 1) {
      settledHeight.current = to;
      return;
    }
    frame.style.height = `${from}px`;
    frame.classList.add("is-animating");
    void frame.offsetHeight;
    frame.style.height = `${to}px`;

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      frame.removeEventListener("transitionend", onEnd);
      window.clearTimeout(timer);
      frame.style.height = "auto";
      frame.classList.remove("is-animating");
      settledHeight.current = frame.getBoundingClientRect().height;
      cleanupRef.current = null;
    };
    const onEnd = (event: TransitionEvent) => {
      if (event.target === frame && event.propertyName === "height") finish();
    };
    frame.addEventListener("transitionend", onEnd);
    // Safety net: a missed transitionend must not leave the height pinned.
    const timer = window.setTimeout(finish, STEP_TRANSITION_MS + 100);
    cleanupRef.current = finish;
  }, [stepKey]);

  useLayoutEffect(() => () => cleanupRef.current?.(), []);

  return (
    <div ref={frameRef} className="twt-step-frame">
      {/* No box of its own (display: contents), so it changes no layout. */}
      <div ref={setBackControlPlace} className="twt-step-back-control" />
      <div
        ref={contentRef}
        className={`twt-step-content ${className}`.trim()}
        data-direction={direction}
        onAnimationEnd={(event) => {
          if (event.target === event.currentTarget)
            event.currentTarget.classList.remove("is-entering");
        }}
      >
        <BackControlPlaceContext.Provider value={backControlPlace}>
          {children}
        </BackControlPlaceContext.Provider>
      </div>
    </div>
  );
}
